import { type FormEvent, useState } from "react"
import { toast } from "sonner"
import { type CurrentUser, api } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ThemeModeSwitcher } from "@/components/ThemeModeSwitcher"
import { BrandMark } from "@/components/BrandMark"

type Props = {
  onLogin: (
    user: CurrentUser,
    machines: string[],
    sshCertificateReady: boolean
  ) => void
}

export function LoginPage({ onLogin }: Props) {
  const [loading, setLoading] = useState(false)
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!username.trim() || !password) return
    setLoading(true)
    try {
      await api.login(username.trim(), password)
      const statusRes = await api.getStatus()
      if (!statusRes.currentUser) {
        throw new Error("Sessão não pôde ser estabelecida.")
      }
      onLogin(
        statusRes.currentUser,
        statusRes.machines,
        statusRes.sshCertificateReady
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Credenciais inválidas.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative flex min-h-svh w-full min-w-0 flex-col bg-background px-4 pb-10 pt-16 sm:px-8 sm:pb-12 sm:pt-20 lg:px-12">
      <div className="absolute right-4 top-4 z-20 sm:right-6 sm:top-6">
        <ThemeModeSwitcher variant="floating" />
      </div>
      {/* Full viewport layer: parent is only as wide as content if children are all absolute; use fixed + 100vw so orbs span the screen */}
      <div
        className="pointer-events-none fixed inset-0 z-0 h-[100dvh] w-screen max-w-[100vw] overflow-hidden"
        aria-hidden
      >
        <div className="absolute -left-[10%] -top-[15%] size-[min(42rem,90vw)] animate-pulse rounded-full bg-primary/[0.07] blur-3xl motion-reduce:animate-none" />
        <div className="absolute -right-[5%] bottom-[-20%] size-[min(48rem,95vw)] animate-pulse rounded-full bg-primary/[0.06] blur-3xl motion-reduce:animate-none [animation-delay:1s]" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-md animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-3 duration-500 fill-mode-both motion-reduce:animate-none">
        <div className="mb-8 flex flex-col items-center gap-3">
          <div className="flex size-12 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 shadow-sm transition-transform duration-300 hover:scale-105 motion-reduce:transition-none">
            <BrandMark className="size-7 text-primary" />
          </div>
          <div className="text-center">
            <h1 className="text-xl font-semibold tracking-tight">VPN ONZ</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Entre com seu usuário local para acessar o painel.
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-xl border bg-card p-6 shadow-sm transition-shadow duration-300 hover:shadow-md motion-reduce:transition-none"
        >
          <div className="space-y-2">
            <Label htmlFor="username">Usuário</Label>
            <Input
              id="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Senha</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <>
                <span className="size-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                Entrando...
              </>
            ) : (
              "Entrar"
            )}
          </Button>
        </form>

        <p className="mt-4 text-center font-mono text-[10px] tracking-widest text-muted-foreground/50 uppercase">
          ONZ · VPN Management Panel
        </p>
      </div>
    </div>
  )
}
